/**
 * Phase 50, BulkImportContext
 *
 * Lifts the bulk-backfill state (queue, running flag, done-summary, batch
 * password) out of the BulkBackfillPanel and up to the App shell. That
 * way, when a user navigates away mid-batch:
 *   • In-flight setState calls still land (setters bound here keep working).
 *   • Returning to the import page restores the queue + progress.
 *   • A small "imports in progress" pill renders in the shell while a
 *     batch is running.
 *
 * Design notes:
 *   • We keep queue items in React state (not localStorage) because the
 *     `file` field is a `File` object, not serialisable. Persistence
 *     across full page reloads is therefore intentionally NOT supported;
 *     refreshing the page does cancel in-flight requests. Navigating
 *     between routes (the common case the user described) is supported.
 *   • The batch password lives in a ref (not state) so it never re-renders
 *     anything and never leaks into props.
 *   • The actions (start / addFiles / processOne) still live inside
 *     BulkBackfillPanel because they need `useParseJobs`, `useAuth`, etc.
 *     The provider gives them stable setters + access to the persistent
 *     queue across mount/unmount cycles.
 */
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const BulkImportContext = createContext(null);

export function BulkImportProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);
  const [doneSummary, setDoneSummary] = useState(null);
  // batchPasswordRef holds the MOST RECENT password (back-compat for the
  // submission path). passwordCacheRef holds EVERY password the user has
  // typed during this batch, we try them silently against each new
  // encrypted file before re-prompting. This is the "drop 12 HDFC PDFs,
  // ask once" magic.
  const batchPasswordRef = useRef(null);
  const passwordCacheRef = useRef([]);

  // Stable helpers, consumers don't have to re-derive these on every render.
  const updateItem = useCallback((id, patch) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((id) => {
    setQueue((q) => q.filter((it) => it.id !== id));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setDoneSummary(null);
    batchPasswordRef.current = null;
    passwordCacheRef.current = [];
  }, []);

  const value = {
    queue, setQueue, updateItem, removeItem, clearQueue,
    running, setRunning,
    doneSummary, setDoneSummary,
    batchPasswordRef,
    passwordCacheRef,
  };

  return (
    <BulkImportContext.Provider value={value}>
      {children}
    </BulkImportContext.Provider>
  );
}

export function useBulkImport() {
  const ctx = useContext(BulkImportContext);
  if (!ctx) {
    throw new Error('useBulkImport must be used inside a BulkImportProvider.');
  }
  return ctx;
}
